import { createWebHistory, createRouter, START_LOCATION } from "vue-router";
import routes from './routes';
import appConfig from "../../app.config";
import keycloakService, { PERCORSI_PUBBLICI } from '@/services/keycloak';
import sicurezzaService from '@/services/sicurezzaService';
import userService from '@/services/userService';
import permessiService from '@/services/permessiService';
import { visibleApps } from '@/apps';
import { vediCome } from '@/services/vediCome';
import toastService from '@/services/toastService';

/** La pagina e la chiave di features di /account/me che la apre. */
const PAGINE_DI_UNA_FUNZIONE = [
  ['/devices', 'serrature'],
  ['/access-links', 'serrature'],
  ['/pulizie', 'pulizie'],
  ['/clienti', 'clienti'],
  ['/settings/chiavi-api', 'sito'],
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  mode: 'history',
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0, left: 0 };
    }
  },
});

// Public routes that don't require authentication
// '/invito/' e' pubblica perche' chi riceve il link spesso non ha ancora un account: mandarlo
// al modulo d'accesso significherebbe chiedergli di entrare per scoprire a cosa sta dicendo di si'.
// La lista sta in keycloak.js e vale anche li' (isOnPublicRoute): erano due copie, e quella di
// keycloak.js aveva perso '/oauth/' e '/paga/'.
const publicRoutes = PERCORSI_PUBBLICI;

// Il launcher in questa scheda del browser e' gia' stato proposto: ricaricando il cruscotto (F5, o il
// tasto Aggiorna) si resta sul cruscotto. Una scheda nuova, invece, riparte dal launcher.
const CHIAVE_LAUNCHER = "zoelia_launcher_proposto";

function launcherGiaProposto() {
  try {
    return sessionStorage.getItem(CHIAVE_LAUNCHER) === "1";
  } catch (e) {
    return false;
  }
}

function ricordaLauncherProposto() {
  try {
    sessionStorage.setItem(CHIAVE_LAUNCHER, "1");
  } catch (e) {
    // Senza memoria della scheda si ripropone a ogni ricaricamento: un fastidio, non un guasto.
  }
}

/**
 * Se questa navigazione verso / e' un ingresso, e non un clic dentro Zoelia. Tutti i collegamenti di
 * Zoelia portano a /: il logo, le briciole, il cambio di spazio. Se / portasse sempre al launcher, chi
 * amministra non arriverebbe piu' al cruscotto. Quindi conta da dove si arriva: dall'accesso, dal
 * secondo fattore, o da fuori (la prima pagina caricata in questa scheda).
 */
function eUnIngresso(from) {
  if (from.path === '/login' || from.path === '/secondo-fattore') return true;
  return from === START_LOCATION && !launcherGiaProposto();
}

// Authentication guard - waits for Keycloak to initialize
router.beforeEach(async (to, from, next) => {
  const isPublicRoute = publicRoutes.some(route => to.path.startsWith(route));

  // Wait for Keycloak to initialize before checking auth
  if (!keycloakService.isInitialized()) {
    await keycloakService.waitForInit();
  }

  const isAuthenticated = keycloakService.isAuthenticated();

  if (!isPublicRoute && !isAuthenticated) {
    // Redirect to login if trying to access protected route
    return next({ path: '/login', query: { redirect: to.fullPath } });
  }

  if (to.path === '/login' && isAuthenticated) {
    // Redirect to home if already authenticated
    return next({ path: '/' });
  }

  // Il secondo fattore, prima di ogni altra guardia.
  //
  // Chi ha la password ma non ha ancora dato il codice non deve atterrare da nessuna parte: il
  // server nega tutto tranne pochi percorsi, quindi qualunque pagina si aprirebbe piena di 403.
  // Si va dove il giro si completa.
  //
  // NON e' la sicurezza — quella e' il filtro lato server, che risponde 403 comunque — ma e' cio'
  // che fa la differenza fra "mi chiede un codice" e "l'applicazione e' rotta". Lo stato lo tiene
  // sicurezzaService, riempito da chi ha fatto l'accesso o, a freddo, da main.js.
  if (isAuthenticated && !isPublicRoute && to.path !== '/secondo-fattore'
      && sicurezzaService.statoAccesso.richiesto) {
    return next({ path: '/secondo-fattore', query: { redirect: to.fullPath } });
  }

  // E il contrario: chi ci finisce senza averne bisogno non ci resta.
  if (to.path === '/secondo-fattore' && isAuthenticated
      && sicurezzaService.statoAccesso.chiesto
      && !sicurezzaService.statoAccesso.richiesto) {
    // La stringa e non { path }: la forma a oggetto viene interpretata e la query
    // viene BUTTATA, quindi chi veniva da /pms?data=...&vista=mese ci tornerebbe nudo,
    // senza filtri ne' date. E' il percorso che si attraversa davvero ogni volta che
    // api.js rimbalza qui per un 403, quindi il parametro perso si nota subito.
    return next(to.query.redirect || '/');
  }

  // "Vedi come": il profilo e' la sicurezza della persona (password, secondo fattore), e non si tocca al
  // posto suo. Il server lo rifiuta comunque: qui si evita di aprire una pagina che non servirebbe a niente.
  if (to.path === '/profile' && vediCome.attivo()) {
    toastService.warning("Il profilo non si apre durante \"Vedi come\": è la sicurezza dell'altra persona.", 'Vedi come');
    return next(from.matched.length ? false : { path: '/' });
  }

  // Le app a parte. NON e' la sicurezza: le API della console rispondono 403 a chi non e' SUPER_ADMIN
  // con l'app di autenticazione, comunque ci arrivi. Qui si decide solo dove atterrare.
  //
  // Chi vede una sola app non passa mai dal launcher, e a / trova Zoelia come sempre: per lui questo
  // blocco non cambia niente. La condizione e' scritta per intero: nell'app per telefoni sparisce.
  if (process.env.VUE_APP_PIATTAFORMA !== "mobile" && isAuthenticated && !isPublicRoute
      && to.path !== '/secondo-fattore') {
    const versoAccessi = to.path === '/accessi' || to.path.startsWith('/accessi/');
    if (to.path === '/hub' || versoAccessi || (to.path === '/' && eUnIngresso(from))) {
      const funzioni = await userService.getFeatures();
      if (to.path === '/' && visibleApps(funzioni).length > 1) {
        ricordaLauncherProposto();
        return next({ path: '/hub' });
      }
      if (to.path === '/hub' && visibleApps(funzioni).length < 2) {
        return next({ path: '/' });
      }
      if (versoAccessi) {
        // Il riquadro lo vede anche chi non ha ancora l'app di autenticazione: gli si spiega cosa manca,
        // invece di mandarlo su pagine che risponderebbero tutte 403.
        if (funzioni.consoleServeApp && to.path !== '/accessi/serve-app') {
          return next({ path: '/accessi/serve-app' });
        }
        if (!funzioni.consoleServeApp && funzioni.console && to.path === '/accessi/serve-app') {
          return next({ path: '/accessi/panoramica' });
        }
        if (!funzioni.console && !funzioni.consoleServeApp) {
          return next({ path: '/' });
        }
      }
    }
  }

  // Il portale dei proprietari (F6, §13.2 di f6-progetto.md), dopo il secondo fattore e prima del ruolo.
  //
  // Chi e' un accesso del portale (features.portaleProprietario: un PROPRIETARIO invitato dalla scheda del
  // proprietario, nello spazio attivo col modulo acceso) sta nel portale: da qualunque altra pagina lo si porta
  // su /portale. Restano sue solo /profile (la sicurezza: secondo fattore, dispositivi fidati) e /secondo-fattore;
  // le pagine pubbliche (l'invito, l'uscita) non passano di qui. Senza questa riga atterrerebbe sul calendario
  // di oggi (la pagina iniziale del ruolo), che con l'ambito vuoto non gli mostra niente.
  //
  // E' l'UNICA riga che cambia la navigazione, e solo per chi ha l'accesso: per tutti gli altri — il proprietario
  // entrato dalla Squadra, ogni spazio senza modulo — la funzione e' falsa e non succede niente. Il profilo ridotto
  // del secondo fattore non porta features: la guardia qui sopra ha gia' mandato la persona a dare il codice.
  if (isAuthenticated && !isPublicRoute && to.path !== '/secondo-fattore' && to.path !== '/profile'
      && to.path !== '/portale' && !to.path.startsWith('/portale/')) {
    const funzioni = await userService.getFeatures();
    // Chi amministra la piattaforma non resta chiuso fuori dalle sue app, anche se nello spazio attivo e'
    // un accesso del portale.
    const versoPiattaforma = funzioni.console && (to.path === '/hub' || to.path.startsWith('/accessi'));
    if (funzioni.portaleProprietario && !versoPiattaforma) {
      return next({ path: '/portale' });
    }
  }

  // Feature guard: check if route requires a specific feature
  if (to.meta?.requiredFeature && isAuthenticated) {
    const features = await userService.getFeatures();
    if (!features[to.meta.requiredFeature]) {
      return next({ path: '/' });
    }
  }

  // La partita IVA ha la gestione professionale al posto del report rendita: "false" e non "assente", col backend di prima si entra.
  if (to.path.startsWith('/rental-report') && isAuthenticated) {
    const features = await userService.getFeatures();
    if (features.reportRendita === false) {
      return next({ path: '/' });
    }
  }

  // Le pagine di una funzione tolta con una regola: la voce di menu non c'e', ma ci si arriva da un link o da una notifica. "=== false" e non
  // "assente", come sopra. Chi ha quella pagina come casa (la governante con le pulizie) va altrove, o rimbalzerebbe in tondo.
  const chiusa = isAuthenticated && PAGINE_DI_UNA_FUNZIONE.find(([p]) => to.path === p || to.path.startsWith(`${p}/`));
  if (chiusa) {
    const features = await userService.getFeatures();
    if (features[chiusa[1]] === false) {
      const profilo = await userService.getProfile();
      const casa = permessiService.paginaIniziale(profilo?.ruolo || null);
      return next({ path: casa === chiusa[0] ? '/profile' : '/' });
    }
  }

  // Guardia sul ruolo nello spazio attivo.
  //
  // NON e' la sicurezza: quella sta sul server e risponde 403 comunque. Serve a non far
  // atterrare nessuno su una pagina che si riempirebbe di errori — una governante mandata sul
  // cruscotto vedrebbe quattro messaggi rossi e una schermata vuota, che sembra un guasto e non
  // un permesso mancante. Chi non puo' stare qui viene portato dove puo' stare.
  // La guardia del ruolo salta il secondo fattore, e non e' ridondante con la tabella dei
  // percorsi: quella risponde alla domanda "questo ruolo puo' aprire questa pagina", e qui la
  // pagina non e' una sezione dell'applicazione ma un passaggio di identita'. Soprattutto, il
  // profilo con il fattore in sospeso arriva RIDOTTO — senza ruolo — quindi chiederlo qui non
  // servirebbe a niente e rischierebbe di mandare la persona altrove.
  //
  // Le pagine segnate soloChiScrive (i moduli di creazione e modifica, i preventivi) chiedono in
  // piu' la scrittura dell'area: leggerla non basta, perche' li' non c'e' niente da guardare e
  // tutto quello che si puo' fare finirebbe in un 403 al salvataggio.
  if (isAuthenticated && !isPublicRoute && to.path !== '/secondo-fattore') {
    const profilo = await userService.getProfile();
    const ruolo = profilo?.ruolo || null;
    const negata = !permessiService.puoAprire(ruolo, to.path)
      || (to.meta?.soloChiScrive && !permessiService.puoScrivere(ruolo, to.path));
    if (ruolo && negata) {
      const destinazione = permessiService.paginaIniziale(ruolo);
      // Se nemmeno la destinazione e' consentita ci si ferma qui invece di rimbalzare in tondo.
      if (to.path !== destinazione && permessiService.puoAprire(ruolo, destinazione)) {
        return next({ path: destinazione });
      }
    }
  }

  next();
});

// Handle chunk load errors after deploy (old cached JS tries to load new chunks)
router.onError((error) => {
  if (error.name === 'ChunkLoadError' || error.message?.includes('Loading chunk')) {
    window.location.reload();
  }
});

// Page title and beforeResolve hooks
router.beforeResolve(async (routeTo, routeFrom, next) => {
  try {
    for (const route of routeTo.matched) {
      await new Promise((resolve, reject) => {
        if (route.meta && route.meta.beforeResolve) {
          route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
            if (args.length) {
              next(...args);
              reject(new Error('Redirected'));
            } else {
              resolve();
            }
          });
        } else {
          resolve();
        }
      });
    }
  } catch (error) {
    return;
  }

  // Set page title
  if (routeTo.meta.title) {
    document.title = routeTo.meta.title + ' | ' + appConfig.title;
  }

  next();
});

export default router;
